import type {
  BackendPet,
  Pet,
  PetKind,
  TapeColor,
  Temperament,
  GoodWith,
  EnergyLevel,
  Regime,
  LifeStage,
} from '../types';
import { illustrationFromId } from './illustrationFromId';

/**
 * BackendPet → Pet. Builds the editorial card-shape components consume.
 * Keep this the only place that touches BackendPet outside of petApi.ts.
 */
export function adaptPet(be: BackendPet): Pet {
  const kind: PetKind = be.type === 'CAT' ? 'cat' : 'dog';
  const female = be.gender === 'FEMALE';
  const ref = shortRef(be.id);
  const ageBand = ageBandFromLifeStage(be.lifeStage);
  const tag = `${kindLabel(kind, female)} · ${AGE_BAND_LABEL[ageBand]}`;
  const facts = buildFacts(be, female);
  const line = buildLine(be);
  const description = buildDescription(be);
  const traits = buildTraits(be);
  const tapeColor = pickTape(be.id);
  const photoUrl = be.photoUrls?.[0];
  const energyLabel = be.energyLevel ? ENERGY_LABEL[be.energyLevel] : null;

  return {
    // identity
    id: be.id,
    ref,
    slug: be.id, // D4: id-as-slug for v1

    // headline
    name: be.name,
    kind,
    tag,
    facts,
    line,
    description,
    traits,
    badge: ref,
    tapeColor,

    // visuals
    photoUrl,
    photoUrls: be.photoUrls ?? [],
    illustration: illustrationFromId(be.id, kind, be.ageMonths),

    // detail
    ageMonths: be.ageMonths,
    ageBand,
    ageBandLabel: AGE_BAND_LABEL[ageBand],
    gender: be.gender,
    genderLabel: female ? 'Fêmea' : 'Macho',
    breeds: be.breeds ?? [],
    regimes: be.regimes ?? [],
    temperaments: be.temperaments ?? [],
    goodWith: be.goodWith ?? [],
    energyLevel: be.energyLevel,
    energyLabel,
    history: be.history,
    healthRequirements: be.healthRequirements,
  };
}

export function adaptPets(beList: BackendPet[]): Pet[] {
  return beList.map(adaptPet);
}

// ── age band ────────────────────────────────────────────────────────────────

export type AgeBand = Pet['ageBand'];

export const AGE_BAND_LABEL: Record<AgeBand, string> = {
  filhote: 'Filhote',
  jovem: 'Jovem',
  adulto: 'Adulto',
  senior: 'Sénior',
  desconhecida: 'Idade indeterminada',
};

/** Map a server-supplied LifeStage to the frontend AgeBand. */
function ageBandFromLifeStage(ls: LifeStage | null): AgeBand {
  if (ls == null) return 'desconhecida';
  switch (ls) {
    case 'FILHOTE': return 'filhote';
    case 'JOVEM':   return 'jovem';
    case 'ADULTO':  return 'adulto';
    case 'SENIOR':  return 'senior';
  }
}

/**
 * Formats an age in months into Portuguese.
 *   null/undefined → null
 *   1..11 months   → "1 mês" / "3 meses"
 *   exact years    → "1 ano" / "5 anos"
 *   years+months   → "1 ano e 6 meses" / "2 anos e 1 mês"
 */
export function formatAgePt(months: number | null | undefined): string | null {
  if (months == null) return null;
  if (months < 12) {
    return months === 1 ? '1 mês' : `${months} meses`;
  }
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yearStr = years === 1 ? '1 ano' : `${years} anos`;
  if (rem === 0) return yearStr;
  const monthStr = rem === 1 ? '1 mês' : `${rem} meses`;
  return `${yearStr} e ${monthStr}`;
}

// ── helpers ─────────────────────────────────────────────────────────────────

function shortRef(id: string): string {
  // 3-char trailing slice, uppercased — readable as a "ficha" number while
  // staying tied to the real id. Mongo ObjectIds → e.g. 'A2F'.
  return id.slice(-3).toUpperCase();
}

function kindLabel(kind: PetKind, female: boolean): string {
  if (kind === 'cat') return female ? 'Gata' : 'Gato';
  return female ? 'Cadela' : 'Cão';
}

function buildFacts(be: BackendPet, female: boolean): string {
  const parts: string[] = [female ? 'Fêmea' : 'Macho'];
  const ageStr = formatAgePt(be.ageMonths);
  if (ageStr != null) parts.push(ageStr);
  if (be.breeds?.length) parts.push(be.breeds[0]);
  return parts.join(' · ');
}

function buildLine(be: BackendPet): string {
  const parts: string[] = [];
  const ageStr = formatAgePt(be.ageMonths);
  if (ageStr != null) parts.push(ageStr);
  if (be.energyLevel) parts.push(ENERGY_LABEL[be.energyLevel].toLowerCase());
  if (parts.length === 0) return 'à espera';
  return parts.join(' — ');
}

const DESC_LIMIT = 200;

function buildDescription(be: BackendPet): string {
  const text =
    [be.history, be.healthRequirements].filter(Boolean).join(' · ').trim();
  if (!text) return 'Ainda à espera de uma ficha completa — pergunta-nos.';
  return text.length <= DESC_LIMIT ? text : `${text.slice(0, DESC_LIMIT - 1).trimEnd()}…`;
}

function buildTraits(be: BackendPet): string[] {
  const traits: string[] = [];
  if (be.energyLevel) traits.push(ENERGY_LABEL[be.energyLevel].toLowerCase());
  for (const t of be.temperaments) traits.push(TEMPERAMENT_LABEL[t]);
  for (const g of be.goodWith) traits.push(`bom com ${GOODWITH_LABEL[g]}`);
  return traits.slice(0, 4); // chip rows look best at ≤4 in the design
}

export const TEMPERAMENT_LABEL: Record<Temperament, string> = {
  FRIENDLY: 'amigável',
  SHY: 'tímido',
  TERRITORIAL: 'territorial',
  PLAYFUL: 'brincalhão',
  INDEPENDENT: 'independente',
  AFFECTIONATE: 'afetuoso',
  CURIOUS: 'curioso',
  RESERVED: 'reservado',
};

export const GOODWITH_LABEL: Record<GoodWith, string> = {
  DOGS: 'cães',
  CATS: 'gatos',
  PEOPLE: 'pessoas',
};

export const REGIME_LABEL: Record<Regime, string> = {
  FAD: 'FAD · Família de Acolhimento Definitiva',
  FAT: 'FAT · Família de Acolhimento Temporário',
};

export const ENERGY_LABEL: Record<EnergyLevel, string> = {
  LOW: 'Calmo',
  MEDIUM: 'Equilibrado',
  HIGH: 'Energético',
};

const TAPE_COLORS: TapeColor[] = ['terracotta', 'ochre', 'sage'];

function pickTape(id: string): TapeColor {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TAPE_COLORS[Math.abs(h) % TAPE_COLORS.length];
}
